import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  FiBook, 
  FiChevronLeft, 
  FiChevronRight, 
  FiEye, 
  FiEyeOff,
  FiPlus,
  FiX,
  FiSearch
} from 'react-icons/fi';
import { getVersions, getChapter } from '../../utils/bible-api';
import { LIBROS_BIBLIA } from '../../utils/bibliaHelper';
import StrongDictionaryAdvanced from './StrongDictionaryAdvanced';
import QuickNavigation from './QuickNavigation';
import { useVerseSelection } from '../../hooks/useVerseSelection';
import BibleVerseToolbar from './BibleVerseToolbar';
import BibleNoteModal from './BibleNoteModal';
import BibleRelationModal from './BibleRelationModal';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Componente avanzado para leer la Biblia con múltiples paneles
 */
export default function BibleReaderAdvanced() {
  const { user } = useAuth();
  const [versions, setVersions] = useState([]);
  const [modulePermissions, setModulePermissions] = useState([]);
  const [storageStrategy, setStorageStrategy] = useState(null);
  const [panels, setPanels] = useState([
    { id: 1, versionId: null, book: 'Genesis', chapter: 1, verses: [], loading: false }
  ]);
  const [showStrongs, setShowStrongs] = useState(true);
  const [selectedStrong, setSelectedStrong] = useState(null);
  const [showDictionary, setShowDictionary] = useState(false);
  const [showQuickNav, setShowQuickNav] = useState(false);
  const [readingPositions, setReadingPositions] = useState({});
  
  // Historial de navegación para volver de citas bíblicas
  const [navigationHistory, setNavigationHistory] = useState([]);
  
  // Estados para controlar visualización de componentes Strong's
  const [displayOptions, setDisplayOptions] = useState({
    showStrongNumber: true,
    showHebrewText: true,       // ← Nuevo
    showTransliteration: true,  // ← Nuevo
    showLexeme: false,
    showMorpheme: true,
    showPhoneme: false,
    showDerivativeMorphemes: false,
    showFlexiveMorphemes: false
  });
  
  // Ref para rastrear última carga y evitar loops
  const lastLoadedRef = useRef({});

  // Hook de selección de versículos
  const {
    selectedVerses,
    handleVerseClick,
    clearSelection,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    getSelectionReference
  } = useVerseSelection();

  // Estados para modales
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showRelationModal, setShowRelationModal] = useState(false);
  const [highlightColor, setHighlightColor] = useState('#FFFF00');
  const [editingNote, setEditingNote] = useState(null);
  const [editingRelation, setEditingRelation] = useState(null);
  
  // Estados para subrayados, notas y relaciones
  const [highlights, setHighlights] = useState({});  // { 'Genesis_1_1': '#FFFF00', ... }
  const [verseNotes, setVerseNotes] = useState({});  // { 'Genesis_1_1': [{id, title, color}, ...], ... }
  const [verseRelations, setVerseRelations] = useState({});  // { 'Genesis_1_1': [{id, type}, ...], ... }

  // Verificar si algún panel tiene Strong's
  const hasStrongsInPanels = versions.some(v => 
    panels.some(p => p.versionId === v.id && v.hasStrongs)
  );

  // Funciones auxiliares primero (antes de los useEffect)
  const loadReadingPositions = () => {
    const saved = localStorage.getItem('bible-reading-positions');
    if (saved) {
      setReadingPositions(JSON.parse(saved));
    }
  };

  const saveReadingPosition = useCallback((book, chapter) => {
    setReadingPositions(prev => {
      const newPositions = {
        ...prev,
        [book]: chapter
      };
      localStorage.setItem('bible-reading-positions', JSON.stringify(newPositions));
      return newPositions;
    });
  }, []);

  const updatePanel = useCallback((panelId, updates) => {
    setPanels(prevPanels => 
      prevPanels.map(panel => 
        panel.id === panelId ? { ...panel, ...updates } : panel
      )
    );
  }, []);

  /**
   * Cargar subrayados, notas y relaciones para un capítulo específico
   */
  const loadVerseAnnotations = useCallback(async (book, chapter) => {
    if (!storageStrategy) return;
    
    try {
      const { BibleStorageService } = await import('../../utils/bibleStorageHelper');
      const bibleStorage = new BibleStorageService(user, modulePermissions);

      // Cargar subrayados
      const allHighlights = await bibleStorage.getHighlights(book, chapter);
      const chapterHighlights = allHighlights.filter(h => 
        h.book === book && h.chapter === chapter
      );
      
      const highlightsMap = {};
      chapterHighlights.forEach(h => {
        const key = `${h.book}_${h.chapter}_${h.verseStart}`;
        highlightsMap[key] = h.color;
      });
      setHighlights(highlightsMap);

      // Cargar notas
      const allNotes = await bibleStorage.getNotes(book, chapter);
      const chapterNotes = allNotes.filter(n => 
        n.book === book && n.chapter === chapter
      );
      
      const notesMap = {};
      chapterNotes.forEach(n => {
        for (let v = n.verseStart; v <= n.verseEnd; v++) {
          const key = `${n.book}_${n.chapter}_${v}`;
          if (!notesMap[key]) notesMap[key] = [];
          // Guardar el objeto completo para poder editarlo después
          notesMap[key].push(n);
        }
      });
      setVerseNotes(notesMap);

      // Cargar relaciones
      const allRelations = await bibleStorage.getRelations(book, chapter);
      
      // getRelations ya filtra por book y chapter, no necesitamos filtrar de nuevo
      const relationsMap = {};
      allRelations.forEach(r => {
        // Usar verseStart directamente en lugar de parsear verseFrom
        const verseNum = r.verseStart || 1;
        const key = `${book}_${chapter}_${verseNum}`;
        if (!relationsMap[key]) relationsMap[key] = [];
        // Guardar el objeto completo para poder editarlo después
        relationsMap[key].push(r);
      });
      setVerseRelations(relationsMap);

      console.log(`📖 Anotaciones cargadas para ${book} ${chapter}`);
    } catch (error) {
      console.error('❌ Error cargando anotaciones:', error);
    }
  }, [user, modulePermissions, storageStrategy]);

  const loadChapterForPanel = useCallback(async (panelId) => {
    const panel = panels.find(p => p.id === panelId);
    if (!panel || !panel.versionId || !panel.book || !panel.chapter) return;

    // Evitar cargar si ya está cargando
    if (panel.loading) return;

    try {
      updatePanel(panelId, { loading: true });
      const response = await getChapter(panel.versionId, panel.book, panel.chapter);
      updatePanel(panelId, { 
        verses: response.data.verses || [],
        loading: false 
      });
      
      // Guardar posición
      saveReadingPosition(panel.book, panel.chapter);
      
      // Cargar anotaciones (subrayados, notas, relaciones)
      await loadVerseAnnotations(panel.book, panel.chapter);
    } catch (error) {
      console.error('Error al cargar capítulo:', error);
      updatePanel(panelId, { verses: [], loading: false });
    }
  }, [panels, updatePanel, saveReadingPosition, loadVerseAnnotations]);

  const loadVersions = useCallback(async () => {
    try {
      const response = await getVersions();
      const versionsList = response.data || [];
      setVersions(versionsList);
      
      // Asignar primera versión al primer panel si no hay ninguna
      if (versionsList.length > 0 && panels.length > 0 && panels[0].versionId === null) {
        updatePanel(1, { versionId: versionsList[0].id });
      }
    } catch (error) {
      console.error('Error al cargar versiones:', error);
    }
  }, [panels, updatePanel]);

  // Cargar permisos del usuario y determinar estrategia de almacenamiento
  useEffect(() => {
    const loadUserPermissions = async () => {
      if (!user) {
        // Usuario no autenticado, usar local
        setModulePermissions([]);
        setStorageStrategy('local');
        return;
      }

      try {
        const apiModule = await import('../../utils/api');
        const api = apiModule.default;
        const response = await api.get(`/admin/users/${user.id}/permissions`);
        const permissions = response.data.data || [];
        setModulePermissions(permissions);

        // La estrategia se determina automáticamente en BibleStorageService
        setStorageStrategy('ready');
        
        console.log('📦 Permisos de módulo cargados:', permissions);
      } catch (error) {
        console.error('Error cargando permisos:', error);
        // Fallback a local
        setModulePermissions([]);
        setStorageStrategy('local');
      }
    };

    loadUserPermissions();
  }, [user]);

  // Cargar versiones y posiciones guardadas
  useEffect(() => {
    loadVersions();
    loadReadingPositions();
  }, [loadVersions]);

  // Cargar versículos cuando cambia versión, libro o capítulo
  useEffect(() => {
    panels.forEach(panel => {
      if (!panel.versionId || !panel.book || !panel.chapter) return;
      
      // Crear key único para este estado del panel
      const loadKey = `${panel.id}-${panel.versionId}-${panel.book}-${panel.chapter}`;
      
      // Solo cargar si el estado cambió
      if (lastLoadedRef.current[panel.id] !== loadKey) {
        lastLoadedRef.current[panel.id] = loadKey;
        loadChapterForPanel(panel.id);
      }
    });
  }, [panels, loadChapterForPanel]);

  // Recargar anotaciones cuando el usuario o permisos cambien
  useEffect(() => {
    const reloadAnnotations = async () => {
      const firstPanel = panels[0];
      if (firstPanel?.book && firstPanel?.chapter) {
        await loadVerseAnnotations(firstPanel.book, firstPanel.chapter);
      }
    };
    
    reloadAnnotations();
  }, [user, modulePermissions, panels, loadVerseAnnotations]);

  const addPanel = useCallback(() => {
    if (panels.length >= 3) {
      alert('Máximo 3 paneles permitidos');
      return;
    }

    const newPanel = {
      id: Math.max(...panels.map(p => p.id)) + 1,
      versionId: versions.length > panels.length ? versions[panels.length].id : versions[0]?.id,
      book: panels[0].book,
      chapter: panels[0].chapter,
      verses: [],
      loading: false
    };

    setPanels([...panels, newPanel]);
  }, [panels, versions]);

  const removePanel = useCallback((panelId) => {
    if (panels.length === 1) {
      alert('Debe haber al menos un panel');
      return;
    }
    setPanels(panels.filter(p => p.id !== panelId));
  }, [panels]);

  const handleStrongClick = useCallback((strongNumber) => {
    setSelectedStrong(strongNumber);
    setShowDictionary(true);
  }, []);

  const handleQuickNavigation = useCallback((book, chapter, verse = null) => {
    panels.forEach(panel => {
      updatePanel(panel.id, { book, chapter });
    });
    setShowQuickNav(false);
  }, [panels, updatePanel]);

  const changeBook = useCallback((panelId, newBook) => {
    // Restaurar última posición si existe
    const savedChapter = readingPositions[newBook] || 1;
    updatePanel(panelId, { book: newBook, chapter: savedChapter });
  }, [readingPositions, updatePanel]);

  const navigateChapter = useCallback((panelId, direction) => {
    const panel = panels.find(p => p.id === panelId);
    if (!panel) return;

    const newChapter = panel.chapter + direction;
    if (newChapter < 1) return;
    
    updatePanel(panelId, { chapter: newChapter });
  }, [panels, updatePanel]);

  const syncAllPanels = useCallback((sourcePanel) => {
    panels.forEach(panel => {
      if (panel.id !== sourcePanel.id) {
        updatePanel(panel.id, {
          book: sourcePanel.book,
          chapter: sourcePanel.chapter
        });
      }
    });
  }, [panels, updatePanel]);

  // Navegar a una cita bíblica desde el diccionario
  const handleNavigateToVerse = useCallback((bookAbbr, chapter, verse) => {
    // Guardar posición actual en el historial
    const currentPanel = panels[0];
    if (currentPanel) {
      setNavigationHistory(prev => [...prev, {
        book: currentPanel.book,
        chapter: currentPanel.chapter
      }]);
    }

    // Convertir abreviatura a nombre completo del libro
    const bookMap = {
      'Gén': 'Genesis', 'Éxo': 'Exodus', 'Lev': 'Leviticus', 'Núm': 'Numbers',
      'Deu': 'Deuteronomy', 'Jos': 'Joshua', 'Jue': 'Judges', 'Rut': 'Ruth',
      '1Sa': '1Samuel', '2Sa': '2Samuel', '1Re': '1Kings', '2Re': '2Kings',
      '1Cr': '1Chronicles', '2Cr': '2Chronicles', 'Esd': 'Ezra', 'Neh': 'Nehemiah',
      'Est': 'Esther', 'Job': 'Job', 'Sal': 'Psalms', 'Pro': 'Proverbs',
      'Ecl': 'Ecclesiastes', 'Cnt': 'SongofSolomon', 'Isa': 'Isaiah',
      'Jer': 'Jeremiah', 'Lam': 'Lamentations', 'Eze': 'Ezekiel', 'Dan': 'Daniel',
      'Ose': 'Hosea', 'Joe': 'Joel', 'Amó': 'Amos', 'Abd': 'Obadiah',
      'Jon': 'Jonah', 'Miq': 'Micah', 'Nah': 'Nahum', 'Hab': 'Habakkuk',
      'Sof': 'Zephaniah', 'Hag': 'Haggai', 'Zac': 'Zechariah', 'Mal': 'Malachi',
      'Mat': 'Matthew', 'Mar': 'Mark', 'Luc': 'Luke', 'Jua': 'John',
      'Hch': 'Acts', 'Rom': 'Romans', '1Co': '1Corinthians', '2Co': '2Corinthians',
      'Gál': 'Galatians', 'Efe': 'Ephesians', 'Fil': 'Philippians', 'Col': 'Colossians',
      '1Te': '1Thessalonians', '2Te': '2Thessalonians', '1Ti': '1Timothy', '2Ti': '2Timothy',
      'Tit': 'Titus', 'Flm': 'Philemon', 'Heb': 'Hebrews', 'Stg': 'James',
      '1Pe': '1Peter', '2Pe': '2Peter', '1Jn': '1John', '2Jn': '2John',
      '3Jn': '3John', 'Jud': 'Jude', 'Apo': 'Revelation'
    };

    const fullBookName = bookMap[bookAbbr] || bookAbbr;

    // Navegar en todos los paneles
    panels.forEach(panel => {
      updatePanel(panel.id, {
        book: fullBookName,
        chapter: parseInt(chapter)
      });
    });
  }, [panels, updatePanel]);

  // Volver a la posición anterior
  const handleGoBack = useCallback(() => {
    if (navigationHistory.length > 0) {
      const previous = navigationHistory[navigationHistory.length - 1];
      setNavigationHistory(prev => prev.slice(0, -1));
      
      panels.forEach(panel => {
        updatePanel(panel.id, {
          book: previous.book,
          chapter: previous.chapter
        });
      });
    }
  }, [navigationHistory, panels, updatePanel]);

  // Funciones para manejar acciones de versículos seleccionados
  const handleAddNote = useCallback(() => {
    if (selectedVerses.length === 0) return;
    setShowNoteModal(true);
  }, [selectedVerses]);

  const handleHighlight = useCallback(async (color) => {
    if (selectedVerses.length === 0) return;
    
    if (!storageStrategy) {
      alert('Cargando configuración...');
      return;
    }
    
    try {
      const { BibleStorageService } = await import('../../utils/bibleStorageHelper');
      const bibleStorage = new BibleStorageService(user, modulePermissions);
      
      const panel = panels[0];
      if (!panel) return;

      // Para cada versículo seleccionado, verificar si ya tiene subrayado
      for (const verseNum of selectedVerses) {
        const verseRef = `${panel.book} ${panel.chapter}:${verseNum}`;
        
        // Obtener subrayados existentes del capítulo
        const existingHighlights = await bibleStorage.getHighlights(panel.book, panel.chapter);
        
        // Buscar si existe subrayado para este versículo específico
        const existingForVerse = existingHighlights.filter(h => 
          h.verseStart === verseNum && h.verseEnd === verseNum
        );
        
        // Si existe, eliminarlo (solo un color por versículo)
        for (const existing of existingForVerse) {
          await bibleStorage.deleteHighlight(existing.id);
        }
        
        // Crear nuevo subrayado
        const highlight = {
          userId: user?.id || 0,
          book: panel.book,
          chapter: panel.chapter,
          verseStart: verseNum,
          verseEnd: verseNum,
          color: color,
          verseRef: verseRef,
          createdAt: new Date().toISOString()
        };

        await bibleStorage.addHighlight(highlight);
        console.log(`✅ Subrayado agregado (${bibleStorage.useBackend ? 'Backend' : 'Local'}):`, highlight);
      }
      
      // Recargar anotaciones para mostrar los cambios
      await loadVerseAnnotations(panel.book, panel.chapter);
      
      clearSelection();
      console.log(`✅ ${selectedVerses.length} versículo(s) subrayado(s) con color ${color}`);
    } catch (error) {
      console.error('❌ Error al guardar subrayado:', error);
      console.error('Detalles del error:', error.message, error.stack);
      alert('Error al guardar el subrayado: ' + error.message);
    }
  }, [selectedVerses, panels, clearSelection, user, modulePermissions, storageStrategy, loadVerseAnnotations]);

  const handleAddRelation = useCallback(() => {
    if (selectedVerses.length === 0) return;
    setEditingRelation(null); // Nueva relación
    setShowRelationModal(true);
  }, [selectedVerses]);

  const handleEditNote = useCallback((note) => {
    setEditingNote(note);
    setShowNoteModal(true);
  }, []);

  const handleEditRelation = useCallback((relation) => {
    setEditingRelation(relation);
    setShowRelationModal(true);
  }, []);

  const handleViewNotes = useCallback(() => {
    // Navegar a la pestaña de notas
    alert('Funcionalidad de ver notas - redirigir a pestaña de notas');
  }, []);

  return (
    <div className="space-y-4">
      {/* Controles Globales */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg">
              <FiBook className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Lectura Bíblica
              </h2>
              <p className="text-sm text-gray-600">
                {panels.length} {panels.length === 1 ? 'versión' : 'versiones'} activas
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Botón Volver */}
            {navigationHistory.length > 0 && (
              <button
                onClick={handleGoBack}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                title="Volver a la lectura anterior"
              >
                <FiChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Volver</span>
              </button>
            )}

            {/* Navegación Rápida */}
            <button
              onClick={() => setShowQuickNav(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              title="Navegación rápida"
            >
              <FiSearch className="w-4 h-4" />
              <span className="hidden sm:inline">Ir a...</span>
            </button>

            {/* Toggle Strong */}
            {hasStrongsInPanels && (
              <button
                onClick={() => setShowStrongs(!showStrongs)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  showStrongs
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {showStrongs ? <FiEye className="w-4 h-4" /> : <FiEyeOff className="w-4 h-4" />}
                <span className="hidden sm:inline">Strong</span>
              </button>
            )}

            {/* Agregar Panel */}
            <button
              onClick={addPanel}
              disabled={panels.length >= 3}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              title="Agregar versión"
            >
              <FiPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Agregar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Opciones de Visualización de Strong's */}
      {showStrongs && hasStrongsInPanels && (
        <div className="bg-white border-b border-gray-200 px-6 py-3">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium text-gray-700">
              Mostrar:
            </span>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={displayOptions.showStrongNumber}
                onChange={(e) => setDisplayOptions(prev => ({
                  ...prev,
                  showStrongNumber: e.target.checked
                }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Número Strong</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={displayOptions.showHebrewText}
                onChange={(e) => setDisplayOptions(prev => ({
                  ...prev,
                  showHebrewText: e.target.checked
                }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Texto Hebreo</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={displayOptions.showTransliteration}
                onChange={(e) => setDisplayOptions(prev => ({
                  ...prev,
                  showTransliteration: e.target.checked
                }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Transliteración</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={displayOptions.showLexeme}
                onChange={(e) => setDisplayOptions(prev => ({
                  ...prev,
                  showLexeme: e.target.checked
                }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Lexema</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={displayOptions.showMorpheme}
                onChange={(e) => setDisplayOptions(prev => ({
                  ...prev,
                  showMorpheme: e.target.checked
                }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Morfema</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={displayOptions.showPhoneme}
                onChange={(e) => setDisplayOptions(prev => ({
                  ...prev,
                  showPhoneme: e.target.checked
                }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                disabled={true}
                title="No disponible en esta versión"
              />
              <span className="text-sm text-gray-400">Fonema</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={displayOptions.showDerivativeMorphemes}
                onChange={(e) => setDisplayOptions(prev => ({
                  ...prev,
                  showDerivativeMorphemes: e.target.checked
                }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                disabled={true}
                title="No disponible en esta versión"
              />
              <span className="text-sm text-gray-400">Morfemas Derivativos</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={displayOptions.showFlexiveMorphemes}
                onChange={(e) => setDisplayOptions(prev => ({
                  ...prev,
                  showFlexiveMorphemes: e.target.checked
                }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                disabled={true}
                title="No disponible en esta versión"
              />
              <span className="text-sm text-gray-400">Morfemas Flexivos</span>
            </label>
          </div>
        </div>
      )}

      {/* Container principal con paneles y diccionario */}
      <div className="flex flex-col gap-4">
        {/* Paneles de Lectura */}
        <div className={`grid grid-cols-1 ${panels.length === 2 ? 'lg:grid-cols-2' : panels.length === 3 ? 'lg:grid-cols-3' : ''} gap-4`}>
          {panels.map((panel) => (
            <PanelReader
              key={panel.id}
              panel={panel}
              versions={versions}
              showStrongs={showStrongs}
              displayOptions={displayOptions}
              onUpdate={(updates) => updatePanel(panel.id, updates)}
              onRemove={() => removePanel(panel.id)}
              onNavigate={(direction) => navigateChapter(panel.id, direction)}
              onBookChange={(book) => changeBook(panel.id, book)}
              onStrongClick={handleStrongClick}
              onSync={() => syncAllPanels(panel)}
              canRemove={panels.length > 1}
              selectedVerses={selectedVerses}
              onVerseClick={handleVerseClick}
              onMouseDown={handleDragStart}
              onMouseUp={handleDragEnd}
              onMouseMove={handleDragOver}
              highlights={highlights}
              verseNotes={verseNotes}
              verseRelations={verseRelations}
              onEditNote={handleEditNote}
              onEditRelation={handleEditRelation}
            />
          ))}
        </div>

        {/* Panel de Diccionario Strong (parte inferior) */}
        {showDictionary && selectedStrong && (
          <div className="w-full">
            <StrongDictionaryAdvanced
              strongNumber={selectedStrong}
              onClose={() => setShowDictionary(false)}
              onNavigateToVerse={handleNavigateToVerse}
              displayOptions={displayOptions}
            />
          </div>
        )}
      </div>

      {/* Navegación Rápida */}
      {showQuickNav && (
        <QuickNavigation
          onNavigate={handleQuickNavigation}
          onClose={() => setShowQuickNav(false)}
          currentBook={panels[0]?.book}
          currentChapter={panels[0]?.chapter}
        />
      )}

      {/* Toolbar de Versículos Seleccionados */}
      {selectedVerses.length > 0 && (
        <BibleVerseToolbar
          selectedCount={selectedVerses.length}
          reference={getSelectionReference(panels[0]?.book, panels[0]?.chapter)}
          onAddNote={handleAddNote}
          onAddHighlight={handleHighlight}
          onAddRelation={handleAddRelation}
          onCancel={clearSelection}
        />
      )}

      {/* Modal de Notas */}
      {showNoteModal && storageStrategy && (
        <BibleNoteModal
          isOpen={showNoteModal}
          onClose={() => {
            setShowNoteModal(false);
            setEditingNote(null);
          }}
          book={panels[0]?.book}
          chapter={panels[0]?.chapter}
          verses={editingNote ? [editingNote.verseStart] : selectedVerses}
          reference={editingNote ? `${editingNote.book} ${editingNote.chapter}:${editingNote.verseStart}` : getSelectionReference(panels[0]?.book, panels[0]?.chapter)}
          initialNote={editingNote}
          user={user}
          modulePermissions={modulePermissions}
          onSave={async () => {
            // Recargar anotaciones
            if (panels[0]) {
              await loadVerseAnnotations(panels[0].book, panels[0].chapter);
            }
            setShowNoteModal(false);
            setEditingNote(null);
            clearSelection();
          }}
        />
      )}

      {/* Modal de Relaciones */}
      {showRelationModal && storageStrategy && (
        <BibleRelationModal
          isOpen={showRelationModal}
          onClose={() => {
            setShowRelationModal(false);
            setEditingRelation(null);
          }}
          book={panels[0]?.book}
          chapter={panels[0]?.chapter}
          verses={editingRelation ? [editingRelation.verseStart] : selectedVerses}
          sourceReference={editingRelation ? `${editingRelation.book} ${editingRelation.chapter}:${editingRelation.verseStart}` : getSelectionReference(panels[0]?.book, panels[0]?.chapter)}
          initialRelation={editingRelation}
          user={user}
          modulePermissions={modulePermissions}
          onSave={async () => {
            // Recargar anotaciones
            if (panels[0]) {
              await loadVerseAnnotations(panels[0].book, panels[0].chapter);
            }
            setShowRelationModal(false);
            setEditingRelation(null);
            clearSelection();
          }}
        />
      )}
    </div>
  );
}

/**
 * Panel individual de lectura
 */
function PanelReader({ 
  panel, 
  versions, 
  showStrongs,
  displayOptions,
  onUpdate, 
  onRemove, 
  onNavigate,
  onBookChange,
  onStrongClick,
  onSync,
  canRemove,
  selectedVerses,
  onVerseClick,
  onMouseDown,
  onMouseUp,
  onMouseMove,
  highlights,
  verseNotes,
  verseRelations,
  onEditNote,
  onEditRelation
}) {
  const currentVersion = versions.find(v => v.id === panel.versionId);
  const scrollContainerRef = useRef(null);
  
  // No necesito estado en este componente, el ref es suficiente

  // Manejar scroll sincronizado (opcional - el usuario puede activarlo con el botón sync)
  const handleScroll = (e) => {
    // Este evento se puede usar para sincronizar scroll con otros paneles
    // Por ahora, solo registramos la posición
    if (scrollContainerRef.current) {
      const scrollPercentage = scrollContainerRef.current.scrollTop / 
        (scrollContainerRef.current.scrollHeight - scrollContainerRef.current.clientHeight);
      // Aquí se podría emitir este valor para sincronizar otros paneles
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header del Panel */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <select
            value={panel.versionId || ''}
            onChange={(e) => onUpdate({ versionId: parseInt(e.target.value) })}
            className="bg-white text-gray-900 px-3 py-1.5 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-300"
          >
            {versions.map((version) => (
              <option key={version.id} value={version.id}>
                {version.abbreviation}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-1">
            <button
              onClick={onSync}
              className="p-1.5 text-white hover:bg-blue-500 rounded transition-colors"
              title="Sincronizar con otros paneles"
            >
              🔄
            </button>
            {canRemove && (
              <button
                onClick={onRemove}
                className="p-1.5 text-white hover:bg-red-500 rounded transition-colors"
                title="Cerrar panel"
              >
                <FiX className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Navegación */}
        <div className="grid grid-cols-3 gap-2">
          <select
            value={panel.book}
            onChange={(e) => onBookChange(e.target.value)}
            className="bg-white text-gray-900 px-2 py-1.5 rounded text-sm"
          >
            {Object.values(LIBROS_BIBLIA)
              .filter((value, index, self) => self.indexOf(value) === index)
              .map((libro) => (
                <option key={libro} value={libro}>
                  {libro}
                </option>
              ))}
          </select>

          <div className="flex items-center gap-1">
            <button
              onClick={() => onNavigate(-1)}
              disabled={panel.chapter <= 1}
              className="p-1.5 bg-white rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiChevronLeft className="w-4 h-4 text-gray-700" />
            </button>
            <input
              type="number"
              value={panel.chapter}
              onChange={(e) => onUpdate({ chapter: parseInt(e.target.value) || 1 })}
              min="1"
              className="flex-1 px-2 py-1.5 bg-white rounded text-center text-sm"
            />
            <button
              onClick={() => onNavigate(1)}
              className="p-1.5 bg-white rounded hover:bg-gray-100"
            >
              <FiChevronRight className="w-4 h-4 text-gray-700" />
            </button>
          </div>

          <div className="text-white text-sm font-medium flex items-center justify-center bg-blue-800 rounded">
            Cap. {panel.chapter}
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="p-4 max-h-[600px] overflow-y-auto"
      >
        {panel.loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {!panel.loading && panel.verses.length > 0 && (
          <div className="space-y-2">
            {panel.verses.map((verse) => {
              const verseKey = `${panel.book}_${panel.chapter}_${verse.Verse}`;
              const highlightColor = highlights[verseKey];
              const notes = verseNotes[verseKey] || [];
              const relations = verseRelations[verseKey] || [];
              
              return (
                <VerseDisplay
                  key={verse.Verse}
                  verse={verse}
                  showStrongs={showStrongs && currentVersion?.hasStrongs}
                  displayOptions={displayOptions}
                  onStrongClick={onStrongClick}
                  isSelected={selectedVerses.includes(verse.Verse)}
                  onVerseClick={onVerseClick}
                  onMouseDown={onMouseDown}
                  onMouseUp={onMouseUp}
                  onMouseMove={onMouseMove}
                  highlightColor={highlightColor}
                  notes={notes}
                  relations={relations}
                  onEditNote={onEditNote}
                  onEditRelation={onEditRelation}
                />
              );
            })}
          </div>
        )}

        {!panel.loading && panel.verses.length === 0 && (
          <div className="text-center py-12">
            <FiBook className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 text-sm">
              No se encontraron versículos
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Componente para mostrar un versículo con Strong's inline
 */
function VerseDisplay({ 
  verse, 
  showStrongs, 
  displayOptions, 
  onStrongClick,
  isSelected,
  onVerseClick,
  onMouseDown,
  onMouseUp,
  onMouseMove,
  highlightColor,
  notes = [],
  relations = [],
  onEditNote,
  onEditRelation
}) {
  // Dividir el texto en palabras
  const words = (verse.text || verse.Scripture || '').split(/\s+/);
  const strongs = verse.strongs || [];

  const handleClick = (e) => {
    // Siempre procesar el click para toggle
    onVerseClick(verse.Verse, e.shiftKey);
  };

  const handleMouseDown = (e) => {
    // Solo iniciar drag si es click izquierdo y no es Shift
    if (e.button === 0 && !e.shiftKey) {
      onMouseDown(verse.Verse);
    }
  };

  const handleMouseEnter = (e) => {
    onMouseMove(verse.Verse);
  };

  const handleMouseUp = (e) => {
    onMouseUp(verse.Verse);
  };

  // Determinar color de fondo (prioridad: seleccionado > subrayado > hover)
  const getBackgroundStyle = () => {
    if (isSelected) {
      return 'bg-blue-200 border-2 border-blue-500';
    }
    if (highlightColor) {
      return `border-2 border-transparent`;
    }
    return 'hover:bg-blue-50 border-2 border-transparent';
  };

  return (
    <div className="relative flex gap-2">
      {/* Franja de iconos (notas y relaciones) - Similar a imagen 7 */}
      {(notes.length > 0 || relations.length > 0) && (
        <div className="flex flex-col gap-1 py-1 px-1 bg-red-100 border-l-2 border-red-400 rounded">
          {notes.map((note, idx) => (
            <button
              key={`note-${note.id || idx}`}
              onClick={(e) => {
                e.stopPropagation();
                onEditNote && onEditNote(note);
              }}
              className="w-6 h-6 rounded flex items-center justify-center text-xs shadow-sm hover:scale-110 transition-transform"
              style={{ backgroundColor: note.color || '#FEF08A' }}
              title={note.title || 'Nota sin título'}
            >
              📝
            </button>
          ))}
          {relations.map((relation, idx) => (
            <button
              key={`rel-${relation.id || idx}`}
              onClick={(e) => {
                e.stopPropagation();
                onEditRelation && onEditRelation(relation);
              }}
              className="w-6 h-6 bg-purple-500 rounded flex items-center justify-center text-xs text-white shadow-sm hover:scale-110 transition-transform"
              title={`Relación: ${relation.title || 'Sin título'}`}
            >
              🔗
            </button>
          ))}
        </div>
      )}

      {/* Versículo principal */}
      <div 
        className={`flex-1 flex gap-3 p-2 rounded transition-colors group cursor-pointer select-none ${getBackgroundStyle()}`}
        style={highlightColor && !isSelected ? { backgroundColor: highlightColor } : {}}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseEnter={handleMouseEnter}
        onMouseUp={handleMouseUp}
      >
        <span className={`font-bold min-w-[2rem] text-sm ${
          isSelected ? 'text-blue-800' : 'text-blue-600'
        }`}>
          {verse.Verse}
        </span>
      
      <div className="flex-1">
        {/* Texto con Strong's inline */}
        <div className="text-gray-800 leading-relaxed text-sm">
          <div className="flex flex-wrap gap-x-2 gap-y-1 items-baseline">
            {words.map((word, idx) => {
              const strongItem = strongs[idx];
              const hasStrong = showStrongs && strongItem;

              return (
                <div key={idx} className="inline-flex flex-col items-start">
                  {/* Palabra */}
                  <span className="text-gray-900">
                    {word}
                  </span>
                  
                  {/* Strong's y morfología debajo de la palabra */}
                  {hasStrong && (
                    <div className="inline-flex flex-col items-start gap-0.5 mt-0.5">
                      {/* Primera línea: Número Strong, Lexema, Morfema */}
                      <div className="inline-flex items-center gap-1 text-[10px]">
                        {/* Número Strong */}
                        {displayOptions.showStrongNumber && (
                          <button
                            onDoubleClick={() => onStrongClick(strongItem.strong)}
                            className="font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            title={`${strongItem.strong} - Doble clic para ver definición`}
                          >
                            {strongItem.strong}
                          </button>
                        )}
                        
                        {/* Lexema (placeholder - futuro) */}
                        {displayOptions.showLexeme && (
                          <span className="text-purple-600 italic" title="Lexema">
                            (lex)
                          </span>
                        )}
                        
                        {/* Morfema/Morfología */}
                        {displayOptions.showMorpheme && strongItem.morphology && (
                          <span className="text-green-600 font-mono" title="Morfología">
                            {strongItem.morphology}
                          </span>
                        )}
                        
                        {/* Fonema (placeholder - futuro) */}
                        {displayOptions.showPhoneme && (
                          <span className="text-orange-600" title="Fonema">
                            (fon)
                          </span>
                        )}
                        
                        {/* Morfemas Derivativos (placeholder - futuro) */}
                        {displayOptions.showDerivativeMorphemes && (
                          <span className="text-pink-600" title="Morfemas Derivativos">
                            (der)
                          </span>
                        )}
                        
                        {/* Morfemas Flexivos (placeholder - futuro) */}
                        {displayOptions.showFlexiveMorphemes && (
                          <span className="text-indigo-600" title="Morfemas Flexivos">
                            (flex)
                          </span>
                        )}
                      </div>
                      
                      {/* Segunda línea: Texto Hebreo */}
                      {displayOptions.showHebrewText && strongItem.hebrewText && (
                        <div 
                          className="text-[11px] text-blue-700 font-semibold"
                          dir="rtl"
                          style={{ fontFamily: 'SBL Hebrew, David, Narkisim, Times New Roman' }}
                          title="Texto Hebreo"
                        >
                          {strongItem.hebrewText}
                        </div>
                      )}
                      
                      {/* Tercera línea: Transliteración */}
                      {displayOptions.showTransliteration && strongItem.transliteration && (
                        <div 
                          className="text-[10px] text-green-700 italic"
                          title="Transliteración"
                        >
                          {strongItem.transliteration}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

